import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { CreateLayoutRequest } from '../../types';
import apiService from '../../services/api';

interface FormErrors {
  [key: string]: string;
}

const CreateLayoutPage: React.FC = () => {
  const [formData, setFormData] = useState<CreateLayoutRequest>({
    name: '',
    location: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    postal_code: '',
    description: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Layout name is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (!formData.postal_code.trim()) {
      newErrors.postal_code = 'Postal code is required';
    } else if (!/^\d{6}$/.test(formData.postal_code)) {
      newErrors.postal_code = 'Postal code must be 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.createLayout(formData);
      navigate(`/layouts/${response.data.id}`, {
        state: { message: 'Layout created successfully!' }
      });
    } catch (err: any) {
      setErrors({
        submit: err.message || 'Failed to create layout'
      });
    } finally {
      setLoading(false);
    }
  };

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Puducherry'
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/layouts')}
          className="btn-outline"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Layouts
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create New Layout</h1>
          <p className="mt-2 text-gray-600">Add a new plot layout to your system</p>
        </div>
      </div>

      {/* Form */}
      <div className="card p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-danger-50 border border-danger-200 rounded-md p-4">
              <div className="text-sm text-danger-700">{errors.submit}</div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="form-label">
                  Layout Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className={`form-input mt-1 ${errors.name ? 'border-red-300' : ''}`}
                  placeholder="Enter layout name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.name && <p className="form-error mt-1">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="location" className="form-label">
                  Location <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  className={`form-input mt-1 ${errors.location ? 'border-red-300' : ''}`}
                  placeholder="e.g., Sector 15, Near Metro Station"
                  value={formData.location}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.location && <p className="form-error mt-1">{errors.location}</p>}
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Address Information</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="address" className="form-label">
                  Full Address <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="address"
                  name="address"
                  rows={3}
                  className={`form-input mt-1 ${errors.address ? 'border-red-300' : ''}`}
                  placeholder="Enter complete address"
                  value={formData.address}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                {errors.address && <p className="form-error mt-1">{errors.address}</p>}
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <div>
                  <label htmlFor="city" className="form-label">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    className={`form-input mt-1 ${errors.city ? 'border-red-300' : ''}`}
                    placeholder="Enter city"
                    value={formData.city}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  {errors.city && <p className="form-error mt-1">{errors.city}</p>}
                </div>

                <div>
                  <label htmlFor="state" className="form-label">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="state"
                    name="state"
                    className={`form-input mt-1 ${errors.state ? 'border-red-300' : ''}`}
                    value={formData.state}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="">Select State</option>
                    {indianStates.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                  {errors.state && <p className="form-error mt-1">{errors.state}</p>}
                </div>

                <div>
                  <label htmlFor="postal_code" className="form-label">
                    Postal Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="postal_code"
                    name="postal_code"
                    className={`form-input mt-1 ${errors.postal_code ? 'border-red-300' : ''}`}
                    placeholder="6-digit postal code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    maxLength={6}
                    disabled={loading}
                  />
                  {errors.postal_code && <p className="form-error mt-1">{errors.postal_code}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="country" className="form-label">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  className="form-input mt-1 bg-gray-50"
                  value={formData.country}
                  disabled
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
            <div>
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                className="form-input mt-1"
                placeholder="Enter layout description, amenities, features..."
                value={formData.description}
                onChange={handleInputChange}
                disabled={loading}
              />
              <p className="mt-1 text-sm text-gray-500">
                Optional: Describe the layout features, amenities, and other details
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/layouts')}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="loading-spinner mr-2"></div>
                  Creating...
                </div>
              ) : (
                'Create Layout'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Help Section */}
      <div className="card p-6 bg-blue-50 border-blue-200">
        <h3 className="text-lg font-medium text-blue-900 mb-2">Next Steps</h3>
        <p className="text-blue-700 text-sm mb-3">
          After creating the layout, you can:
        </p>
        <ul className="text-blue-700 text-sm space-y-1 list-disc list-inside">
          <li>Add plots to the layout using bulk creation</li>
          <li>Assign users to manage this layout</li>
          <li>Generate reports for the layout</li>
          <li>Track plot sales and status changes</li>
        </ul>
      </div>
    </div>
  );
};

export default CreateLayoutPage; 